<script>
    import { afterUpdate } from 'svelte';
    import clickOutside from 'svelte-outside-click';
    import { animateScroll } from 'svelte-scrollto-element';
    import Emoji from '../../../Emoji.svelte';
    import StatusIcon from '../StatusIcon.svelte';
    import Dropdown from '../../../_wrappers/Dropdown.svelte';
    import BottomSheet from '../../../_wrappers/BottomSheet.svelte';
    import DropdownInputContent from './DropdownInputContent.svelte';
    import DropdownInputActionButtons from './DropdownInputActionButtons.svelte';
    import DeviceDetector from 'svelte-device-detector';
    import clsx from 'clsx';
    import uniqueId from 'lodash/uniqueId';

    // Utils
    import { getBorderRadius } from '$lib/global/helper/border-radius';

    export let placeholder = '';
    export let showEmoji = false;
    export let emoji = '';
    export let required = false;
    export let invalid = false;
    export let pristine = true;
    export let touched = false;
    export let type = 'text';
    export let isFirst = false;
    export let isLast = false;
    export let finalFormInput = {};
    export let customOptions = {
        language: '',
        multiSelect: false,
        twoColumn: false,
        choices: [],
    };
    export let errorMessage = '';

    const dropdownId = uniqueId('dropdown');

    let wrapperClasses;
    let inputClasses;
    let inputWrapperClasses;
    let chevronSvgClasses;

    let showError = invalid && touched;
    let showSuccess = !invalid && touched && !pristine;
    let showCheckmark = ['email', 'phone', 'url'].includes(type);
    let selectedChoices = [];
    let visualInputValue;
    let open = false;

    // Default language to 'de'
    const language = customOptions.language || 'de';
    const { twoColumn, multiSelect, choices } = customOptions;

    afterUpdate(() => {
        showError = invalid && touched;
        showSuccess = !invalid && touched && !pristine;
        showCheckmark = ['email', 'phone', 'url'].includes(type);
    });

    const handleInputClick = () => {
        open ? hideDropdown() : showDropdown();
    };

    const showDropdown = () => {
        scrollIntoView();
        finalFormInput.onFocus();
        open = true;
    };

    const hideDropdown = () => {
        open = false;
        finalFormInput.onBlur();
    };

    const handleClickOutside = () => {
        if (!open) return;

        hideDropdown();
    };

    const handleGlobalKeyDown = (event) => {
        const travelBack = event.shiftKey && event.key === 'Tab';
        if (travelBack) {
            open && hideDropdown();
        }
        // Handle space or enter click
        if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            open ? hideDropdown() : showDropdown();
        }
    };

    const handleSave = (event) => {
        event.preventDefault();
        finalFormInput.onChange(selectedChoices.sort().join(';'));
        visualInputValue = selectedChoices.sort().join(', ');
        hideDropdown();
    };

    const handleClear = () => {
        selectedChoices = [];
        visualInputValue = undefined;
        finalFormInput.onChange('');
    };

    const handleSelect = (event, optionValue) => {
        event.preventDefault();

        if (multiSelect) {
            if (selectedChoices.includes(optionValue)) {
                selectedChoices = selectedChoices.filter((choice) => choice !== optionValue);
            } else {
                selectedChoices = [...selectedChoices, optionValue];
            }
        } else {
            finalFormInput.onChange(optionValue);
            hideDropdown();
        }
    };

    const scrollIntoView = () => {
        const dropdown = document.querySelector(`#${dropdownId}`);
        if (dropdown) {
            // Offset the height of the input field
            animateScroll?.scrollTo({ element: dropdown, offset: -64 });
        }
    };

    const borderRadius = getBorderRadius('fieldsWrapper');

    $: wrapperClasses = clsx(borderRadius, 'wrapper relative', {
        invalid: invalid,
    });

    $: inputWrapperClasses = clsx(
        borderRadius,
        'input-wrapper flex relative box-border relative transition-all overflow-hidden',
        {
            ['rounded-b-none']: !isLast,
            ['rounded-t-none']: !isFirst,
            ['h-[90px]']: showError,
            ['border-b']: isFirst && isLast,
        },
    );

    $: inputClasses = clsx(
        borderRadius,
        'input placeholder-gray-400 box-border w-full pl-6 pr-12 border-0 border-l border-r border-t border-black/10 mb-0 font-ui text-base align-middle bg-white transition-all appearance-none font-light focus:outline-none focus:bg-gray-50',
        {
            ['rounded-b-none']: !isLast,
            ['rounded-t-none']: !isFirst,
            ['border-b']: ((invalid && isFirst) || !isFirst) && isLast,
            ['pl-[70px]']: showEmoji,
            ['h-[90px] pb-[26px]']: showError,
            ['h-[64px]']: !showError,
            ['pr-24']: !required && pristine,
        },
    );

    $: chevronSvgClasses = clsx(
        'w-4 h-4 absolute top-[26px] text-gray-500 transition-all duration-[400ms] ease-[cubic-bezier(0.16,1,0.3,1)] transform pointer-events-none cursor-pointer',
        {
            '-rotate-180': !open,
            'rotate-0': open,
            'right-16': showError,
            'right-6': !showError,
        },
    );
</script>

<div class={wrapperClasses} use:clickOutside={handleClickOutside}>
    <div class={inputWrapperClasses}>
        {#if showEmoji}
            <u class="absolute left-6 mt-6 flex h-[18px] select-none items-center no-underline">
                <Emoji {emoji} size="24px" />
            </u>
        {/if}

        {#if !required && !invalid && pristine}
            <u class="absolute right-14 mt-6 text-xs font-light text-gray-500 no-underline">
                (optional)
            </u>
        {/if}

        <StatusIcon {showCheckmark} {showError} {showSuccess} />

        <div
            class="absolute bottom-0 left-0 right-0 top-0 z-10 cursor-pointer"
            on:click={handleInputClick}
        />

        <input
            readonly
            class={inputClasses}
            type="text"
            {placeholder}
            name={finalFormInput.name}
            value={visualInputValue || finalFormInput.value}
            on:blur={finalFormInput.onBlur}
            on:keydown={handleGlobalKeyDown}
            on:input={() => finalFormInput.onChange(finalFormInput.value)}
        />

        <svg
            class={chevronSvgClasses}
            width="14"
            height="8"
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M6.55806 0.433058C6.80214 0.188981 7.19786 0.188981 7.44194 0.433058L13.6919 6.68306C13.936 6.92714 13.936 7.32286 13.6919 7.56694C13.4479 7.81102 13.0521 7.81102 12.8081 7.56694L7 1.75888L1.19194 7.56694C0.947864 7.81102 0.552136 7.81102 0.308058 7.56694C0.0639806 7.32286 0.0639806 6.92714 0.308058 6.68306L6.55806 0.433058Z"
                fill="#6B7280"
            />
        </svg>

        {#if showError}
            <div
                class="absolute left-[70px] top-14 text-xs font-normal leading-tight text-[#ef444a]"
            >
                {errorMessage}
            </div>
        {/if}
    </div>

    <DeviceDetector showInDevice={['mobile', 'tablet']}>
        <BottomSheet
            {open}
            title={placeholder}
            handleClose={hideDropdown}
            actionButtonsComponent={multiSelect ? DropdownInputActionButtons : null}
            actionButtonsProps={{
                disabled: !selectedChoices?.length,
                language,
                twoColumn,
                handleSave,
                handleClear,
                open,
            }}
        >
            <DropdownInputContent
                {choices}
                {selectedChoices}
                value={finalFormInput.value}
                {twoColumn}
                {handleSelect}
                {open}
            />
        </BottomSheet>
    </DeviceDetector>

    <DeviceDetector showInDevice="desktop">
        <Dropdown id={dropdownId} {open} contentStyle="w-full">
            <DropdownInputContent
                {choices}
                {selectedChoices}
                value={finalFormInput.value}
                {twoColumn}
                {handleSelect}
                {open}
            />
            {#if multiSelect}
                <DropdownInputActionButtons
                    disabled={!selectedChoices?.length}
                    {language}
                    {twoColumn}
                    {handleSave}
                    {handleClear}
                    {open}
                />
            {/if}
        </Dropdown>
    </DeviceDetector>
</div>

<style lang="scss">
    .wrapper {
        box-shadow: 0 1px 1px 0 rgba(#000, 0.1);

        :global([placeholder]) {
            @apply truncate;
        }

        .input:focus {
            box-shadow: 0 1px 1px 0 rgba(#000, 0.1);
        }
    }
</style>
